// Updated: 2026-08-14
//
// Migrated to React Query in slice 6 (react-query-notifications, Req 2.3, 4.2, 4.5).
//
// TWO CONSOLIDATIONS LAND HERE:
//
//  1. THE BADGE. It used to read `useUserRelations().unreadNotificationCount`, which the bulk
//     `fetchUserRelations` call sets ONCE per `idToken` for the whole session and which no notification
//     write ever refreshes — so it disagreed with the Notification Center's own badges the moment
//     anything was read or deleted. It now reads `useNotifications()`, the single
//     `['me','notifications','unreadCount']` entry that both write mutations invalidate. Same number by
//     construction: the context field and this endpoint's `count` were read from the SAME DynamoDB record
//     and attribute (see `queries/notification/keys.ts`), so moving the badge changed the number's
//     FRESHNESS, never its definition. Slice 6 left that context field in place, unread; slice 7
//     (react-query-user-relations) removed it when it rewrote `UserRelationsContext` as a derivation over
//     `['me','relations']`. `['me','notifications','unreadCount']` is now the ONLY source of an unread
//     count — a badge must never read one off a bulk payload again, because a payload fetched on an
//     unrelated schedule cannot be invalidated by a notification write, which is exactly how the two
//     badges desynced. There is no field on the relations payload left to point this badge back at.
//
//  2. THE LIST. `fetchNotificationsPaginated(null, idToken)` (this component) and
//     `fetchNotificationsPaginated(null, idToken, 'all')` (the Notification Center's "All" tab) already
//     produced the byte-identical URL, because the transport only sets `category` when it is present and
//     not `'all'`. `normalizeCategory` makes them ONE cache entry and ONE request instead of two private
//     copies in two components' state.
//
// This component reads `data.pages[0].notifications` — PAGE 1 ONLY, deliberately. Sharing the entry means
// the entry can hold pages this component never asked for; reading `pages.flatMap(…)` would make the
// bell's "5 most recent unread" depend on how far the Notification Center had paged. Page 1 keeps the
// render byte-identical to the pre-migration behavior (it only ever had page 1) while still sharing the
// entry and the request (design § D3).
//
// No injected-write prop: single call site (`components/common/navigation/Navigation.tsx:90`), so the
// slice-1 shared-component rule does not apply (design § D6).

import type { NotificationItem } from '@mrb91002/shared-types'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../hooks/useNotifications'
import { useMarkNotificationReadMutation } from '../queries/notification/mutations'
import { useNotificationsQuery } from '../queries/notification/queries'

import { getNotificationIcon } from './pages/NotificationCenter/notificationUtils'

import { Popover } from '@/components/ui/base'
import { Button } from '@/components/ui/base'
import { Box } from '@/components/ui/base'

const NotificationBell: React.FC = () => {
  // Still read for the pre-migration write guard, so a token-less render stays silent (no request AND no
  // log line) exactly as it did. The queries have their own `enabled: !!idToken` gate.
  const { idToken } = useAuth()
  const { unreadCount } = useNotifications()
  const navigate = useNavigate()

  const notificationsQuery = useNotificationsQuery('all')
  const markRead = useMarkNotificationReadMutation()

  // PAGE 1 ONLY — see the module header. Matches the pre-migration single-page fetch exactly.
  const allNotifications: NotificationItem[] = notificationsQuery.data?.pages[0]?.notifications ?? []
  const loading = notificationsQuery.isPending && notificationsQuery.isFetching

  // Show the 5 most recent unread notifications
  const unreadNotifications = allNotifications.filter((n) => !n.read).slice(0, 5)

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    return `${Math.floor(diffInMinutes / 1440)}d ago`
  }

  const handleMarkAsRead = (notification: NotificationItem) => {
    // The pre-migration guard, verbatim: no token or an already-read row issues no request and logs
    // nothing (Req 5.10). Without the `!idToken` half, the mutation would throw and the `onError` below
    // would print a line the pre-migration code never printed.
    if (!idToken || notification.read) return
    markRead.mutate(
      { id: notification.id, category: notification.category, created_at_ms: notification.created_at_ms },
      {
        // The local `setAllNotifications(prev => prev.map(...))` patch is gone: the mutation invalidates
        // the shared list entry, so the read flip arrives by background refetch and the popover is never
        // unmounted. The catch keeps the exact pre-migration log line.
        onError: (err: unknown) => console.error('[NotificationBell] Failed to mark as read:', err),
      },
    )
  }

  const handleActionClick = (action: { url: string }, notification: NotificationItem) => {
    handleMarkAsRead(notification)
    navigate(action.url)
  }

  return (
    <Popover
      position="bottom"
      size="medium"
      triggerType="custom"
      dismissButton={false}
      content={
        <Box>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Box variant="h3" margin={{ bottom: 'none' }}>
              Notifications
            </Box>
            <Button variant="link" onClick={() => navigate('/notifications')} fontSize="12px">
              View all
            </Button>
          </div>
          <div style={{ minWidth: 320, maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <Box padding="m" color="inherit">
                Loading…
              </Box>
            ) : unreadNotifications.length === 0 ? (
              <Box padding="m" color="inherit">
                No unread notifications
              </Box>
            ) : (
              <>
                {unreadNotifications.map((notification) => (
                  <Box
                    key={notification.id}
                    padding="s"
                    margin={{ bottom: 'xxs' }}
                    backgroundColor="grey-100"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleMarkAsRead(notification)}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, maxWidth: '100%' }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{getNotificationIcon(notification.type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            marginBottom: 2,
                            fontSize: 14,
                          }}
                        >
                          {notification.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: '#666',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            marginBottom: 4,
                          }}
                        >
                          {notification.preview}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: '#999' }}>{formatTimeAgo(notification.created_at)}</span>
                          <span style={{ color: '#3b82f6', fontSize: 12 }}>•</span>
                        </div>
                        {notification.actions && notification.actions.length > 0 && (
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {notification.actions.slice(0, 2).map((action, index) => (
                              <button
                                key={index}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  border: '1px solid #d1d5db',
                                  borderRadius: 4,
                                  background: action.style === 'primary' ? 'rgb(59, 130, 135)' : 'white',
                                  color: action.style === 'primary' ? 'white' : '#374151',
                                  cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleActionClick(action, notification)
                                }}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Box>
                ))}
                <Box padding="s" textAlign="center">
                  <Button onClick={() => navigate('/notifications')} variant="link" fontSize="12px">
                    View all notifications
                  </Button>
                </Box>
              </>
            )}
          </div>
        </Box>
      }
    >
      <div style={{ position: 'relative' }}>
        <div style={{ color: '#3B8287', display: 'inline-block' }}>
          <button
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', fontSize: '20px' }}
            aria-label="Notifications"
          >
            🔔
          </button>
        </div>
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: '#ef4444',
              color: 'white',
              borderRadius: '50%',
              fontSize: 11,
              width: 18,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
    </Popover>
  )
}

export default NotificationBell
